import type { PageType } from '@/types/nav-types'

import { encodeExperimentAssignments, type ExperimentAssignments } from './experiment-tracking'

const PAGE_TYPE_CODES: Record<PageType, string> = {
  home: 'h',
  product: 'p',
  compare: 'c',
  directory: 'd',
  story: 's',
  brand: 'b',
  contact: 'o',
  '404': 'x',
}

interface CompactAffiliateTrackingContext {
  pathname: string
  position?: string
  productSlug?: string
  pageType?: PageType
  sessionId?: string | null
  experiments?: ExperimentAssignments
}

const cleanPart = (value: string, fallback: string): string => {
  const cleaned = value
    .trim()
    // Public slugs and tracking positions are hyphenated. Keeping underscores
    // exclusively as field separators makes the reporting parser positional.
    .replaceAll('_', '-')
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return cleaned || fallback
}

const pagePath = (pathname: string): string => {
  const path = pathname.split(/[?#]/, 1)[0].replace(/^\/+|\/+$/g, '')
  if (!path) return 'home'
  // Double hyphens encode route boundaries. Canonical public slugs collapse
  // repeated hyphens, so this is reversible without introducing `/` into a
  // network sub-ID.
  return path
    .split('/')
    .map((segment) => cleanPart(segment, 'x'))
    .join('--')
}

const inferredPageType = (pathname: string, productSlug?: string): PageType => {
  const path = pathname.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/'
  if (path === '/' || /^\/[a-z]{2}$/.test(path)) return 'home'
  if (/\/hearing-aids\/compare(?:\/|$)/.test(path)) return 'compare'

  const finalSegment = path.split('/').filter(Boolean).at(-1)
  if (productSlug && finalSegment === productSlug) return 'product'
  return 'story'
}

export const compactAffiliateSessionId = (sessionId: string | null | undefined): string => {
  if (!sessionId) return '0'
  const compact = sessionId.replace(/[^A-Za-z0-9]/g, '')
  return compact.slice(-12) || '0'
}

/**
 * Build HearingTracker's versioned compact affiliate identifier.
 *
 * Format (most important to least important):
 * ht2_{pageType}_{position}_{pagePath}_{productSlug}_{sessionId}
 * ht3_{experimentTokens}_{pageType}_{position}_{pagePath}_{productSlug}_{sessionId}
 *
 * Length-constrained networks try the historical eight-character session
 * suffix before dropping fields from the right, preserving the reporting
 * dimensions before the optional session correlation ID.
 */
export const buildCompactAffiliateTrackingId = (
  context: CompactAffiliateTrackingContext,
  maxLength?: number
): string => {
  const type = context.pageType ?? inferredPageType(context.pathname, context.productSlug)
  const typeCode = PAGE_TYPE_CODES[type]
  const position = cleanPart(context.position ?? '', 'x')
  const path = pagePath(context.pathname)
  const finalPathSegment = path.split('--').at(-1)
  const product =
    type === 'product' && context.productSlug === finalPathSegment ? 'x' : cleanPart(context.productSlug ?? '', 'x')
  const session = compactAffiliateSessionId(context.sessionId)
  const experiments = encodeExperimentAssignments(context.experiments ?? {})
  const prefix = experiments ? ['ht3', experiments, typeCode] : ['ht2', typeCode]

  const variants = [
    [...prefix, position, path, product, session],
    [...prefix, position, path, product, session.slice(-8)],
    [...prefix, position, path, product],
    [...prefix, position, path],
    [...prefix, position],
    [...prefix, 'x'],
  ].map((parts) => parts.join('_'))

  if (!maxLength) return variants[0]
  return variants.find((value) => value.length <= maxLength) ?? variants.at(-1)!
}
